import {getFeatureCost, getQuotePriceWithTaxes, getTotalPrice, toFixedPrice} from "../helpers";
import {CABINETS_ONLY, EDGE_GROUP} from "../../bvcomponents/kvComponent/core/constants";
import store from "../../store";
import {numberWithCommas} from "../priceFunctions";

const costcoTax = 6; // 6% from products only! (80% of price) skip taxes for labor (20% of price)
const costcoProductPercentage = 80; // (80% of price)

const getCostcoPercents = (storeState) => {
  const {fabricatorTaxes} = storeState.fabricatorState;
  const calculateTax = (Array.isArray(fabricatorTaxes) && fabricatorTaxes[0]?.materialRate
    ? fabricatorTaxes[0].materialRate : costcoTax) / 100;
  const costcoPercent = (Array.isArray(fabricatorTaxes) && fabricatorTaxes[0]?.productPercent
    ? fabricatorTaxes[0].productPercent : costcoProductPercentage) / 100;
  return {calculateTax, costcoPercent}
}

const getCostcoCountertopsCartItems = (cartItems) => {
  return cartItems.filter(cartItem => {
    return cartItem.partId === "center_island_countertop"
      || cartItem.partId === "countertop"
      || (cartItem.partId === "backsplash" && (
        (cartItem.fromWizardGroupId === "18")
        || (cartItem.fromWizardGroupId === "12")
        || (cartItem.fromWizardGroupId === "11")
        || (cartItem.fromWizardGroupId === "187")
        || (cartItem.fromWizardGroupId === "196")
        || (cartItem.fromWizardGroupId === "206")
      ))
      || EDGE_GROUP.some(partId => cartItem.partId === partId)
  })
}

const getCabinetsCartItems = (cartItems) => {
  return cartItems.filter(cartItem => CABINETS_ONLY.some(partId => cartItem.partId === partId))
}

export const getCartItemsPrice = (cartItems) => {
  return cartItems.reduce((materialTotalCost, cartItem) => {
    materialTotalCost += getFeatureCost(cartItem.cartFeature, cartItem.quantity, cartItem.fromQtCategory);
    return materialTotalCost
  }, 0);
}

export const getCostcoShopCartCost = (cartItems) => {
  return toFixedPrice(getCartItemsPrice(getCostcoCountertopsCartItems(cartItems)) * 0.1);
}

/**
 * @param forCartItems {[cartItemInterface]}
 * @param withTaxes {boolean}
 * returns total cost of cabinets
 * */
const getQuoteTotal = (forCartItems = [], withTaxes = false) => {
  if (!forCartItems.length) {
    return 0;
  }
  const totalPrice = getTotalPrice(forCartItems[0].currentQuote);
  return Math.floor(withTaxes ? getQuotePriceWithTaxes(totalPrice) : totalPrice.materialPrice)
}

/**
 * @param cartItems {[cartItemInterface]}
 * @param withTaxes {boolean}
 * returns total cost of cabinets
 * */
const getCabinetsTotal = (cartItems, withTaxes = false) => {
  return getQuoteTotal(getCabinetsCartItems(cartItems), withTaxes);
}

/**
 * @param cartItems {[cartItemInterface]}
 * @param withTaxes {boolean}
 * returns total cost of countertops
 * */
const getCountertopsTotal = (cartItems, withTaxes = false) => {
  return getQuoteTotal(getCostcoCountertopsCartItems(cartItems), withTaxes);
}

export const getCostcoContractPrice = (storeState) => {
  const {cartItems} = storeState.cartState;
  const {calculateTax, costcoPercent} = getCostcoPercents(storeState);

  const cabinetsTotal = getCabinetsTotal(cartItems);
  const countertopsTotal = getCountertopsTotal(cartItems);
  const productTotal = Math.floor((cabinetsTotal + countertopsTotal) * costcoPercent);
  const laborTotal = Math.floor(((cabinetsTotal + countertopsTotal) - productTotal));
  const taxTotal = Math.floor(productTotal * calculateTax); // 6% from product
  return Math.floor(productTotal + laborTotal + taxTotal);
}

const getLineItemProperty = (fieldName, storeState) => {
  const {cartItems} = storeState.cartState;

  const lastDigit = fieldName.slice(-1) - 1; // "1" -> 0

  const nameWithoutNumber = fieldName.slice(0, -1); // "mkdCabinetQuantity"

  const isCabinet = nameWithoutNumber.includes("Cabinet");

  const neededCartItems = isCabinet ? getCabinetsCartItems(cartItems) : getCostcoCountertopsCartItems(cartItems);
  const cartItem = neededCartItems[lastDigit];
  if (!cartItem) {
    return null
  }
  const property = isCabinet ? nameWithoutNumber.replace("mkdCabinet", "") : nameWithoutNumber.replace("mkdCountertop", "");
  switch (property) {
    case "Quantity":
      // return cartItem.quantity;
      return 1
    case "Description":
      return cartItem.cartFeature.getFeatureName();
    case "ModelNumber":
      return cartItem.cartFeature.dbFeature.sku || "-";
    // case "UnitPrice": {
    //   const displayTotal = getFeatureCost(cartItem.cartFeature, 1, cartItem.fromQtCategory);
    //   return numberWithCommas(Math.floor(displayTotal))
    // }
    case "UnitPrice":
    case "Total": {
      const displayTotal = getFeatureCost(cartItem.cartFeature, cartItem.quantity, cartItem.fromQtCategory);
      return numberWithCommas(Math.floor(displayTotal))
    }
    default:
      return null
  }
}

export const getCostcoContractField = (fieldName) => {
  const storeState = store.getState();
  const {cartItems} = storeState.cartState;
  const {calculateTax, costcoPercent} = getCostcoPercents(storeState);

  const lastDigit = fieldName.slice(-1);
  if (!isNaN(+lastDigit)) { // "mkdCabinetQuantity1..5" or "mkdCountertopDescription1..5", total 50 fields
    return getLineItemProperty(fieldName, storeState)
  }

  switch (fieldName) {
    case "mkdRefacingSubtotal": {
      return numberWithCommas(getCabinetsTotal(cartItems));
    }
    case "mkdCountertopSubtotal": {
      return numberWithCommas(getCountertopsTotal(cartItems));
    }
    case "mkdProduct": {
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const countertopsTotal = getCountertopsTotal(cartItems);
      const displayTotal = (cabinetsTotal + countertopsTotal) * costcoPercent;
      return numberWithCommas(Math.floor(displayTotal));
    }
    case "mkdLabor": {
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const countertopsTotal = getCountertopsTotal(cartItems);
      const displayTotal = (cabinetsTotal + countertopsTotal) * costcoPercent;
      const displayTotal2 = (cabinetsTotal + countertopsTotal) - displayTotal;
      return numberWithCommas(Math.ceil(displayTotal2))
    }
    case "mkdTax": {
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const countertopsTotal = getCountertopsTotal(cartItems);
      const productTotal = (cabinetsTotal + countertopsTotal) * costcoPercent;
      const displayTotal = productTotal * calculateTax; // 6% from product
      return numberWithCommas(Math.floor(displayTotal));
    }
    case "mkdContractPrice": {
      // const totalPrice = getTotalPrice();
      // const grandTotal = getQuotePriceWithTaxes(totalPrice);
      // return numberWithCommas(Math.floor(grandTotal));
      return numberWithCommas(getCostcoContractPrice(storeState));
    }
    case "mkdRefacingProductTotal":
    case "costcoCabinetSubtotal":
    case "costcoCabinetTotal": {
      const cabinetsTotal = getCabinetsTotal(cartItems);
      return numberWithCommas(Math.floor(cabinetsTotal));
    }
    case "mkdCountertopProductTotal":
    case "costcoCountertopSubtotal":
    case "costcoCountertopTotal": {
      const countertopsTotal = getCountertopsTotal(cartItems);
      return numberWithCommas(Math.floor(countertopsTotal));
    }
    case "mkdRefacingTax":
    case "costcoCabinetTax": {
      const cabinetsTotal = getCabinetsTotal(cartItems);
      const displayTotal = cabinetsTotal * calculateTax;
      return numberWithCommas(Math.floor(displayTotal));
    }
    case "mkdCountertopTax":
    case "costcoCountertopTax": {
      const countertopsTotal = getCountertopsTotal(cartItems);
      const displayTotal = countertopsTotal * calculateTax;
      return numberWithCommas(Math.floor(displayTotal));
    }
    case "mkdShopCardValue":
      return getCostcoShopCartCost(cartItems);
    case "crAmnt": {
      const displayTotal = getCartItemsPrice(getCabinetsCartItems(cartItems)) * 0.1;
      return numberWithCommas(Math.floor(displayTotal));
    }
    case "ccAmnt": {
      const displayTotal = getCostcoShopCartCost(cartItems);
      return numberWithCommas(Math.floor(displayTotal));
    }
    default:
      return null;
  }
}
